import React from 'react';
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { User } from 'lucide-react';
import RatingBadge from './RatingBadge';
import StarRating from './StarRating';

function QuestionEditor({
  question,
  questionIndex,
  value = {},
  onChange = null,
  ratingOptions = [],
  employeeSelfResponse = null,
  showStars = true,
}) {
  const handleRatingChange = (ratingLabel) => {
    if (onChange) {
      onChange(question.id, 'rating', ratingLabel);

      // Automatically set star score based on rating option position
      if (showStars) {
        const optionIndex = ratingOptions.indexOf(ratingLabel);
        if (optionIndex !== -1) {
          const starScore = optionIndex + 1;
          onChange(question.id, 'starScore', starScore);
        }
      }
    }
  };

  const handleStarChange = (stars) => {
    if (onChange) {
      onChange(question.id, 'starScore', stars);
    }
  };

  const handleCommentChange = (comment) => {
    if (onChange) {
      onChange(question.id, 'comment', comment);
    }
  };

  return (
    <div className="group py-4 sm:py-6 last:pb-6 transition-colors duration-200">
      {/* Question Header */}
      <div className="flex items-start gap-3 sm:gap-4 mb-2">
        <div className="h-6 w-6 sm:h-7 sm:w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
          <span className="text-[10px] sm:text-xs font-semibold text-primary">{questionIndex + 1}</span>
        </div>
        <div className="flex-1">
          <p className="text-sm sm:text-[15px] font-medium text-foreground leading-relaxed">{question.text}</p>
        </div>
      </div>

      {/* Show employee's self-review if manager is reviewing */}
      {employeeSelfResponse && (
        <div className="ml-0 sm:ml-11 mb-4 sm:mb-5 p-3 sm:p-4 rounded-lg bg-muted/50 border border-border/50">
          <Label className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <User className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> Employee's Self-Review
          </Label>
          <div className="space-y-2">
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <RatingBadge 
                label={employeeSelfResponse.rating_label} 
                color="primary" 
                size="sm" 
              />
              {employeeSelfResponse.rating > 0 && (
                <StarRating rating={employeeSelfResponse.rating} size={14} />
              )}
            </div>
            {employeeSelfResponse.comment && (
              <p className="text-xs sm:text-sm text-muted-foreground italic">"{employeeSelfResponse.comment}"</p>
            )}
          </div>
        </div>
      )}

      {/* Rating Pills */}
      <div className="ml-0 sm:ml-11 mb-3 sm:mb-4">
        <Label className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 sm:mb-3 block">
          Rating
        </Label>
        <div className="flex flex-wrap gap-1.5 sm:gap-2">
          {ratingOptions.map((option) => {
            const isSelected = value.rating === option;
            return (
              <button
                key={option}
                type="button"
                onClick={() => handleRatingChange(option)}
                className={`
                  px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all
                  ${isSelected 
                    ? 'bg-primary text-primary-foreground shadow-sm' 
                    : 'bg-background text-muted-foreground border border-border hover:border-primary/50 hover:text-foreground'
                  }
                `}
              >
                {option}
              </button>
            );
          })}
        </div>
      </div>

      {/* Star Score */}
      {showStars && (
        <div className="ml-0 sm:ml-11 mb-3 sm:mb-4">
          <Label className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 sm:mb-3 block">
            Star Score
          </Label>
          <StarRating
            rating={value.starScore || 0}
            size={18}
            interactive={false}
            onChange={handleStarChange}
          />
        </div>
      )}

      {/* Comment */}
      <div className="ml-0 sm:ml-11">
        <Label className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 sm:mb-3 block">
          Comments (optional)
        </Label>
        <Textarea
          placeholder="Provide specific examples and feedback..."
          value={value.comment || ''}
          onChange={(e) => handleCommentChange(e.target.value)}
          className="min-h-[80px] resize-none text-sm"
        />
      </div>
    </div>
  );
}

export default QuestionEditor;
